"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VoixDeLaCommunaut() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isEnglish, setIsEnglish] = useState(false);
  useEffect(() => {
    const checkLang = () => {
      const lang = localStorage.getItem("lacolonne_language") || "fr";
      setIsEnglish(lang === "en");
    };
    checkLang();
    window.addEventListener("storage", checkLang);
    window.addEventListener("languagechange", checkLang);
    return () => {
      window.removeEventListener("storage", checkLang);
      window.removeEventListener("languagechange", checkLang);
    };
  }, []);
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const testimonials = [{
    quote: isEnglish ? "Thanks to La Colonne, I found an active social life again in Edmonton. Their programs helped me integrate while staying proud of my roots." : "Grâce à La Colonne, j'ai retrouvé une vie sociale active à Edmonton. Leurs programmes m'ont aidé à m'intégrer tout en restant fier de mes racines.",
    author: "Victorine Mboudou",
    position: isEnglish ? "Francophone senior, Edmonton" : "Aîné francophone, Edmonton",
    tenure: isEnglish ? "5 years with La Colonne" : "5 ans avec La Colonne",
    meta: isEnglish ? "Member since 2019" : "Membre depuis 2019",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2026-03-26 at 5.01.34 PM.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2026-03-26 at 5.01.34 PM.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2026-03-26 at 5.01.34 PM.jpg-wvc-sizes"
  }, {
    quote: isEnglish ? "The digital literacy workshops changed my life. I can now easily communicate with my family back home." : "Les ateliers de littératie numérique ont changé ma vie. Je peux maintenant communiquer facilement avec ma famille restée au pays.",
    author: "Ebinette Felicite K.",
    position: isEnglish ? "Workshop participant" : "Participante aux ateliers",
    tenure: isEnglish ? "3 years with La Colonne" : "3 ans avec La Colonne",
    meta: isEnglish ? "Member since 2021" : "Membre depuis 2021",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/112310_15d1623afa3023bda8e9117dac8bf63e-30_05_2026 22_59_18.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/112310_15d1623afa3023bda8e9117dac8bf63e-30_05_2026 22_59_18.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/112310_15d1623afa3023bda8e9117dac8bf63e-30_05_2026 22_59_18.jpg-wvc-sizes"
  }, {
    quote: isEnglish ? "Food assistance and intergenerational activities create a true sense of belonging for my multicultural family." : "L'appui alimentaire et les activités intergénérationnelles créent un véritable sentiment d'appartenance pour ma famille multiculturelle.",
    author: isEnglish ? "Traoré Family" : "Famille Traoré",
    position: isEnglish ? "Newcomers" : "Nouveaux arrivants",
    tenure: isEnglish ? "2 years with La Colonne" : "2 ans avec La Colonne",
    meta: isEnglish ? "Members since 2022" : "Membres depuis 2022",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2024-04-02 à 14.26.47_f7f04833tt.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2024-04-02 à 14.26.47_f7f04833tt.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/WhatsApp Image 2024-04-02 à 14.26.47_f7f04833tt.jpg-wvc-sizes"
  }, {
    quote: isEnglish ? "Thank you - we had a great time and made beautiful fascinator hats." : "Merci - on s'est bien amuser et avons créé de beaux chapeau bibi.",
    author: "Isabelle Y.",
    position: isEnglish ? "Creative workshop participant" : "Participante aux ateliers créatifs",
    tenure: isEnglish ? "La Colonne creative workshops" : "Ateliers créatifs La Colonne",
    meta: isEnglish ? "Fascinator hat workshop" : "Atelier chapeau bibi",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692734/img/Merci pour votre.png"
  }];
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    const onReInit = () => {
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap());
    };
    api.on("select", onSelect);
    api.on("reInit", onReInit);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onReInit);
    };
  }, [api]);
  useEffect(() => {
    if (!api || isPaused) return;
    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 7000);
    return () => clearInterval(interval);
  }, [api, isPaused]);
  return <section data-section-id="750" id="voix-de-la-communauté" className="bg-muted text-foreground relative overflow-hidden py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-lg opacity-10 blur-3xl" style={{
      background: "radial-gradient(circle, var(--secondary) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl">
        <div ref={headingRef} className={`mb-12 transition-all duration-700 ease-out md:mb-16 text-center ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs font-medium tracking-[0.3em] uppercase text-muted-foreground">
            {isEnglish ? "Testimonials" : "Témoignages"}
          </p>
          <h2 className="mt-4 font-default font-semibold text-foreground" style={{
          fontSize: "clamp(2rem, 4vw, 3.5rem)",
          lineHeight: 1.2
        }}>
            {isEnglish ? "Real lives. " : "Vies réelles. "}
            <span className="font-serif italic font-normal">
              {isEnglish ? "Concrete impact." : "Impact concret."}
            </span>
          </h2>
        </div>

        <div onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          <Carousel setApi={setApi} opts={{
          loop: true,
          align: "start"
        }} className="w-full">
            <CarouselContent className="-ml-6">
              {testimonials.map((t, i) => <CarouselItem data-index={i} key={i} className="pl-6 basis-full md:basis-1/2 lg:basis-1/3">
                  <div className="group flex h-full flex-col overflow-hidden rounded-2xl bg-card text-card-foreground shadow-lg border border-border/80 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                    <div className="relative h-60 sm:h-64 w-full overflow-hidden bg-muted">
                      <img src={t.image} {...t.srcset ? {
                    "data-wvc-srcset": t.srcset
                  } : {}} {...t.sizes ? {
                    "data-wvc-sizes": t.sizes
                  } : {}} alt={t.author} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute top-3 left-3 px-3 py-1.5 rounded-xl shadow-md" style={{
                    backgroundColor: "var(--secondary)",
                    color: "var(--secondary-foreground)"
                  }}>
                        <span className="font-mono text-[10px] font-semibold tracking-[0.2em] uppercase">
                          {t.tenure}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col justify-between p-6 sm:p-8">
                      <div>
                        <Quote className="mb-3 opacity-20" style={{
                      color: "var(--secondary)"
                    }} size={36} strokeWidth={1.5} />
                        <blockquote className="font-serif italic text-foreground text-base sm:text-lg leading-relaxed">
                          &ldquo;{t.quote}&rdquo;
                        </blockquote>
                      </div>

                      <div className="mt-6 pt-6 border-t border-border/60">
                        <p className="font-default text-base font-semibold text-foreground">
                          {t.author}
                        </p>
                        <p className="mt-1 font-default text-xs sm:text-sm text-muted-foreground">
                          {t.position} &middot; {t.meta}
                        </p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          <div className="mt-12 flex flex-col items-center justify-between gap-6 sm:flex-row">
            <div className="flex items-center gap-2" role="tablist" aria-label={isEnglish ? "Testimonial progress" : "Progression des témoignages"}>
              {Array.from({
              length: count
            }).map((_, i) => <Button data-index={i} key={i} onClick={() => api?.scrollTo(i)} aria-label={isEnglish ? `Go to testimonial ${i + 1}` : `Aller au témoignage ${i + 1}`} variant="ghost" className="h-2.5 px-0 transition-all duration-500 ease-out rounded-2xl" style={{
              width: i === current ? "44px" : "14px",
              backgroundColor: i === current ? "var(--secondary)" : "var(--muted-foreground)",
              opacity: i === current ? 1 : 0.35
            }} />)}
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={() => api?.scrollPrev()} aria-label={isEnglish ? "Previous testimonial" : "Témoignage précédent"} className="h-12 w-12 rounded-2xl border bg-card text-foreground hover:bg-accent shadow-sm transition-colors" style={{
              borderColor: "var(--border)"
            }}>
                <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
              </Button>
              <Button variant="outline" size="icon" onClick={() => api?.scrollNext()} aria-label={isEnglish ? "Next testimonial" : "Témoignage suivant"} className="h-12 w-12 rounded-2xl border bg-card text-foreground hover:bg-accent shadow-sm transition-colors" style={{
              borderColor: "var(--border)"
            }}>
                <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}